const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'packages/patapp_web/src/pages/appointment/BookAppointmentPage.tsx';
import { useIntl } from 'react-intl';
import * as React from 'react';
import { useEffect, useState } from 'react';
import textMap from 'App/pages/appointment/translations';
import { IconClass } from '@cistec/common_web/defs/icons';
import { Page } from '../Page';
import { Button, Divider, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { useDispatch, useSelector } from 'react-redux';
import { Theme, useTheme } from '@mui/material/styles';
import { useHistory } from 'react-router';
import { usePatientData } from 'App/store/patient';
import { currentPatientFromEmailLogin } from '@cistec/common_web/selectors/patientsSelectors';
import { Plan, Slot, Step, TreatmentReasonListItem } from 'App/defs/types';
import { KeyValuePair } from '@cistec/common_web/defs/generalDefs';
import { MessageDescriptor } from '@formatjs/intl';
import { Moment } from 'moment';
import { loadAvailableSchedulingSlotsForPlan, resetAppointmentBookingSlots } from 'App/actions/schedulingSlot';
import { PlandAndTreatmentBlock } from './components/PlanAndTreatmentBlock';
import { SlotSelector } from './components/SlotSelector';
import { CommentField } from './components/CommentField';
import { AppointmentBookingPatientDataFields } from './components/AppointmentBookingPatientDataFields';
import { AppointmentBookingOverview } from './components/AppointmentBookingOverview';
import { css } from '@emotion/css';
import {
    validateAHVRFF,
    validateEmailRFF,
    validateNotEmpty,
} from '@cistec/common_web/components/Forms/utils/validation';
import _ from 'lodash';
import { actionBookAppointment } from 'App/actions/appointment';
import { FeatureConfiguration, useFeatureFlags } from 'App/store/featureFlags';
import {
    selectAvailableSchedulingSlotsForSelectedPlan,
    selectIsAvailableSchedulingSlotsForSelectedPlanLoading,
} from 'App/store/schedulingSlot';
import { Alert } from 'App/components/Alert/Alert';
import { getConfiguration } from 'App/utils/clientSetupGetRessource';

export interface IAppointmentBookingPageProps {}

export type PatientFormData = {
    email: string | null;
    streetAndNr: string | null;
    postalcode: string | null;
    location: string | null;
    country: string | null;
    socialNr: string | null;
};

const cssTextStyle = css`
    padding: 12px 0;

    &:first-child {
        padding: 0;
    }
`;

const cssButtonStyle = css`
    display: flex;
    justify-content: flex-end;
`;

const cssSteps = (theme: Theme) => css`
    margin-top: 17px;

    &:first-child {
        margin-top: 10px;
    }

    ${theme.breakpoints.between(360, 'xs')} {
        flex-basis: 100%;
        flex-grow: 0;
        max-width: 100%;
    }
`;
type FieldConfig = {
    name: string;
    required?: boolean;
    validator?: (value: string) => string | undefined; // Value of string should be id of MessageDescriptor
};

const getFieldsConfig = (featureConfig: FeatureConfiguration) => {
    const fieldConfigs: FieldConfig[] = [];

    if (featureConfig?.appointment?.BookingCommentField) {
        fieldConfigs.push({
            name: 'comment',
            required: featureConfig?.appointment?.BookingCommentRequir,
        });
    }

    if (featureConfig?.appointment?.BookingDataFields) {
        fieldConfigs.push(
            {
                name: 'email',
                required: true,
                validator: validateEmailRFF,
            },
            {
                name: 'streetAndNr',
                required: true,
            },
            {
                name: 'postalcode',
                required: true,
            },
            {
                name: 'location',
                required: true,
            },
            {
                name: 'country',
                required: true,
            },
            {
                name: 'socialNr',
                required: true,
                validator: validateAHVRFF,
            }
        );
    }

    return fieldConfigs;
};

const validateField = (formData: KeyValuePair, field: FieldConfig) => {
    const value = formData[field.name] || '';
    const errors = [];
    if (field.required) {
        const error = validateNotEmpty(value.trim());
        error && errors.push(error);
    }
    if (field.validator) {
        const error = field.validator(value);
        error && errors.push(error);
    }
    return errors.length ? errors : undefined;
};

const getPatientData = (patient: any, notLinkedPatient: any, formData: KeyValuePair) => {
    const sanitizePatientData = (patientData: KeyValuePair): any => {
        return _(patientData).omitBy(_.isUndefined).omitBy(_.isNull).value();
    };

    let patientData = {
        PATNR: patient.get('PATNR'),
        NAME: patient.get('NAME'),
        VORNAME: patient.get('VORNAME'),
        SEX: patient.get('SEX'),
        TELEFON: patient.get('TELEFON'),
        TELEFON_G: patient.get('TELEFON_G'),
        TELEFON_M: patient.get('TELEFON_M'),
        DATE_OF_BIRTH: patient.get('DATE_OF_BIRTH'),
    };

    if (!patientData.PATNR) {
        patientData = {
            ...patientData,
            NAME: patientData.NAME === undefined && (notLinkedPatient?.get('lastname') as any),
            VORNAME: patientData.VORNAME === undefined && (notLinkedPatient?.get('firstname') as any),
            TELEFON_M: patientData.TELEFON_M === undefined && (notLinkedPatient?.get('mobile') as any),
            DATE_OF_BIRTH: patientData.DATE_OF_BIRTH === undefined && (notLinkedPatient?.get('dateofbirth') as any),
        };
    }

    const patientDataFromForm = {
        EMAIL: formData.email,
        LAND: formData.country,
        STRASSE: formData.streetAndNr,
        PLZ: formData.postalcode,
        ORT: formData.location,
        SOZVERSNR: formData.socialNr,
    };

    return {
        ...patientData,
        ...sanitizePatientData(patientDataFromForm),
    };
};

const getDefaultPatientData = (patient: any, notLinkedPatient: any) => ({
    email: patient.get('EMAIL') || notLinkedPatient?.get('email') || '',
    streetAndNr: patient.get('STRASSE') || '',
    postalcode: patient.get('PLZ') || '',
    location: patient.get('ORT') || '',
    country: patient.get('LAND') || '',
    socialNr: patient.get('SOZVERSNR') || '',
});

export const BookAppointmentPage = (props: IAppointmentBookingPageProps) => {
    const dispatch = useDispatch();
    const theme = useTheme();
    const history = useHistory();
    const { formatMessage } = useIntl();

    const [patient] = usePatientData();
    const notLinkedPatient = useSelector(currentPatientFromEmailLogin);
    const defaultPatientData = getDefaultPatientData(patient, notLinkedPatient);

    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
    const [selectedTreatmentReason, setSelectedTreatmentReason] = useState<TreatmentReasonListItem | null>(null);
    const [selectedSlot, setSelectedSlot] = useState<Slot | null>();
    const [formData, setFormData] = useState<KeyValuePair>({
        comment: '',
        ...defaultPatientData,
    });

    const isLoadingSlots = useSelector(selectIsAvailableSchedulingSlotsForSelectedPlanLoading);
    const slots = useSelector(selectAvailableSchedulingSlotsForSelectedPlan);

    const { data: featureFlags } = useFeatureFlags();

    const fieldConfigs: FieldConfig[] = getFieldsConfig(featureFlags || {});
    let firstRender = false;

    useEffect(() => {
        firstRender = true;
    }, []);

    useEffect(() => {
        if (!selectedPlan || !selectedTreatmentReason) {
            dispatch(resetAppointmentBookingSlots());
        }
    }, [selectedPlan, selectedTreatmentReason]);

    const isFieldValid = (formData: KeyValuePair, field: FieldConfig) => !Boolean(validateField(formData, field));

    const isFormValid = (formData: KeyValuePair): boolean => fieldConfigs.every(field => isFieldValid(formData, field));

    const setInputFieldError = (fieldName: string, errorMessage: string | MessageDescriptor | undefined) => {
        setErrors(prevErrors => ({
            ...prevErrors,
            [fieldName]:
                (errorMessage && (typeof errorMessage === 'string' ? errorMessage : formatMessage(errorMessage))) || '',
        }));
    };

    const validateFieldAndSetErrors = (formData: KeyValuePair, field: FieldConfig) => {
        const errors = validateField(formData, field);
        setInputFieldError(field.name, errors && { id: errors[0] });
    };

    const validateFormAndSetErrors = (formData: KeyValuePair) =>
        fieldConfigs.forEach(field => validateFieldAndSetErrors(formData, field));

    const updateFormDataAndSetFieldErrors = (fieldName: string, fieldValue: string) => {
        const newFormData = { ...formData, [fieldName]: fieldValue };
        setFormData(newFormData);
        const field = fieldConfigs.find(field => field.name === fieldName);
        if (field) {
            validateFieldAndSetErrors(newFormData, field);
        }
    };

    const handleCommentChange = (event: any) => {
        const fieldName = 'comment';
        const fieldValue = event.target.value;
        updateFormDataAndSetFieldErrors(fieldName, fieldValue);
    };

    const handleAppointmentBookingInputFieldsChange = (fieldName: keyof PatientFormData, fieldValue: string) => {
        updateFormDataAndSetFieldErrors(fieldName, fieldValue);
    };

    const handlePlanAndTreatmentBlockChange = (plan: Plan, treatmentReason: TreatmentReasonListItem) => {
        setSelectedPlan(plan);
        setSelectedTreatmentReason(treatmentReason);
        setSelectedSlot(null);
    };

    const loadSlots = (fromDate: Moment, amountOfDaysToLoad: number) => {
        if (selectedPlan && selectedTreatmentReason) {
            dispatch(loadAvailableSchedulingSlotsForPlan(selectedPlan!.Icode, amountOfDaysToLoad, 1, fromDate));
        }
    };

    const handleSlotSelect = (slot: Slot | null) => {
        setSelectedSlot(slot);
    };

    const getInfoText = () => {
        const { bookAppointmentInfo } = getConfiguration();
        if (bookAppointmentInfo) {
            return <Alert color="info">{formatMessage(bookAppointmentInfo)}</Alert>;
        } else {
            return;
        }
    };

    const appointmentSteps: Step[] = [
        {
            condition: true,
            disabled: false,
            title: formatMessage(textMap.treatmentReasonText),
            text: formatMessage(textMap.reasonForAppointmentText),
            content: <PlandAndTreatmentBlock onChange={handlePlanAndTreatmentBlockChange} />,
        },
        {
            condition: !!selectedPlan && !!selectedTreatmentReason,
            disabled: false,
            title: formatMessage(textMap.choiceOfDate),
            text: formatMessage(textMap.availableSlots),
            content: (
                <SlotSelector
                    onSlotSelect={handleSlotSelect}
                    requiredMinimumDuration={selectedTreatmentReason?.duration}
                    loadSlots={loadSlots}
                    slots={slots}
                    dataLoading={isLoadingSlots}
                />
            ),
        },
        {
            condition: !!selectedPlan && !!selectedTreatmentReason && !!selectedSlot,
            title: formatMessage(textMap.appointmentReason),
            text: formatMessage(textMap.commentAppointmentReason),
            disabled: !featureFlags.appointment.BookingCommentField,
            content: (
                <CommentField
                    onChange={handleCommentChange}
                    error={errors['comment']}
                    required={featureFlags.appointment.BookingCommentRequir}
                />
            ),
        },
        {
            condition: !!selectedPlan && !!selectedTreatmentReason && !!selectedSlot,
            disabled: !featureFlags.appointment.BookingDataFields,
            title: formatMessage(textMap.addInformation),
            text: formatMessage(textMap.addInformationText),
            content: (
                <AppointmentBookingPatientDataFields
                    defaultValues={defaultPatientData}
                    onChange={handleAppointmentBookingInputFieldsChange}
                    errors={errors}
                />
            ),
        },
        {
            condition: !!selectedPlan && !!selectedTreatmentReason && !!selectedSlot,
            disabled: false,
            title: formatMessage(textMap.chosenDate),
            content: <AppointmentBookingOverview plan={selectedPlan!} slot={selectedSlot!} />,
        },
    ];

    const handleCancelButtonClick = () => {
        history.goBack();
    };

    const canSubmit = () => {
        return !!selectedPlan && !!selectedTreatmentReason && !!selectedSlot && isFormValid(formData);
    };

    const handleBookAppointmentButtonClick = () => {
        if (canSubmit()) {
            dispatch(
                actionBookAppointment(
                    selectedTreatmentReason!,
                    selectedSlot!,
                    getPatientData(patient, notLinkedPatient, formData),
                    notLinkedPatient,
                    formData,
                    history
                )
            );
        } else {
            validateFormAndSetErrors(formData);
        }
    };

    const activeSteps = appointmentSteps.filter(step => step.disabled === false);

    const getStepWrapper = (index: number, step: Step) => {
        return (
            <Grid key={`step_${index}`} size={{ xs: 12 }} className={cssSteps(theme)}>
                <Typography sx={{ color: 'primary.main' }} variant="h6">
                    {`${index + 1}/${activeSteps.length} ${step.title}`}
                </Typography>
                <Divider />
                <div className={cssTextStyle}>
                    <Typography>{step.text}</Typography>
                </div>
                {step.content}
            </Grid>
        );
    };

    const getButtonsBlock = () => {
        return (
            <Grid size={{ sm: 12 }} className={cssButtonStyle}>
                <Button onClick={handleCancelButtonClick}>{formatMessage(textMap.cancel)}</Button>
                <Button
                    disabled={!canSubmit()}
                    color={'primary'}
                    variant={'contained'}
                    onClick={handleBookAppointmentButtonClick}
                >
                    {formatMessage(textMap.requestAppointment)}
                </Button>
            </Grid>
        );
    };

    return (
        <Page title={formatMessage(textMap.cardTitle)} iconClass={IconClass.Calendar}>
            {getInfoText()}
            <Grid container spacing={2}>
                {activeSteps.map((step, index) => step.condition && getStepWrapper(index, step))}
                {!!selectedSlot && !!selectedPlan && !!selectedTreatmentReason && getButtonsBlock()}
            </Grid>
        </Page>
    );
};
